import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";

export type DataPoint = {
  timestamp: number;
  // `null` marks a data gap (outage): the chart breaks the line there.
  value: number | null;
};

export type ChannelDataPoints = {
  [channelId: string]: {
    [variant: string]: {
      data: DataPoint[];
      isLoading: boolean;
      error: Error | null;
      lastUpdated: number;
    };
  };
};

export interface DatapointsStore {
  // State
  channelDataPoints: ChannelDataPoints;

  // Actions
  setChannelDataPoints: (
    channelId: string,
    variant: string,
    data: DataPoint[]
  ) => void;
  setChannelLoading: (
    channelId: string,
    variant: string,
    isLoading: boolean
  ) => void;
  setChannelError: (
    channelId: string,
    variant: string,
    error: Error | null
  ) => void;
  clearChannelData: (channelId: string, variant: string) => void;

  // Computed selectors
  getChannelDataPoints: (channelId: string, variant: string) => DataPoint[];
  getChannelLoading: (channelId: string, variant: string) => boolean;
  getChannelError: (channelId: string, variant: string) => Error | null;
  getChannelLastUpdated: (channelId: string, variant: string) => number;
}

export const useDatapointsStore = create<DatapointsStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      channelDataPoints: {},

      // Actions
      setChannelDataPoints: (
        channelId: string,
        variant: string,
        data: DataPoint[]
      ) => {
        set(
          (state) => ({
            channelDataPoints: {
              ...state.channelDataPoints,
              [channelId]: {
                ...state.channelDataPoints[channelId],
                [variant]: {
                  data,
                  isLoading: false,
                  error: null,
                  lastUpdated: Date.now(),
                },
              },
            },
          }),
          false,
          "setChannelDataPoints"
        );
      },

      setChannelLoading: (
        channelId: string,
        variant: string,
        isLoading: boolean
      ) => {
        set(
          (state) => ({
            channelDataPoints: {
              ...state.channelDataPoints,
              [channelId]: {
                ...state.channelDataPoints[channelId],
                [variant]: {
                  data:
                    state.channelDataPoints[channelId]?.[variant]?.data || [],
                  isLoading,
                  error: null,
                  lastUpdated:
                    state.channelDataPoints[channelId]?.[variant]
                      ?.lastUpdated || Date.now(),
                },
              },
            },
          }),
          false,
          "setChannelLoading"
        );
      },

      setChannelError: (
        channelId: string,
        variant: string,
        error: Error | null
      ) => {
        set(
          (state) => ({
            channelDataPoints: {
              ...state.channelDataPoints,
              [channelId]: {
                ...state.channelDataPoints[channelId],
                [variant]: {
                  data:
                    state.channelDataPoints[channelId]?.[variant]?.data || [],
                  isLoading: false,
                  error,
                  lastUpdated:
                    state.channelDataPoints[channelId]?.[variant]
                      ?.lastUpdated || Date.now(),
                },
              },
            },
          }),
          false,
          "setChannelError"
        );
      },

      clearChannelData: (channelId: string, variant: string) => {
        set(
          (state) => {
            const newChannelDataPoints = { ...state.channelDataPoints };
            if (newChannelDataPoints[channelId]) {
              delete newChannelDataPoints[channelId][variant];
              if (Object.keys(newChannelDataPoints[channelId]).length === 0) {
                delete newChannelDataPoints[channelId];
              }
            }
            return { channelDataPoints: newChannelDataPoints };
          },
          false,
          "clearChannelData"
        );
      },

      // Computed selectors
      getChannelDataPoints: (channelId: string, variant: string) => {
        const { channelDataPoints } = get();
        return channelDataPoints[channelId]?.[variant]?.data || [];
      },

      getChannelLoading: (channelId: string, variant: string) => {
        const { channelDataPoints } = get();
        return channelDataPoints[channelId]?.[variant]?.isLoading || false;
      },

      getChannelError: (channelId: string, variant: string) => {
        const { channelDataPoints } = get();
        return channelDataPoints[channelId]?.[variant]?.error || null;
      },

      getChannelLastUpdated: (channelId: string, variant: string) => {
        const { channelDataPoints } = get();
        return channelDataPoints[channelId]?.[variant]?.lastUpdated || 0;
      },
    })),
    {
      name: "datapoints-store",
    }
  )
);
